import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, Table, Typography, Spin, DatePicker } from 'antd';
import { DollarOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { getAllPayments, getAllCoursesByRole } from '../../../Constants/Api/functions/function';
import dayjs from 'dayjs';

import './styles.scss';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const RevenueAnalysis = () => {
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const [isLoading, setIsLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [courses, setCourses] = useState([]);
    const [stats, setStats] = useState({
        totalRevenue: 0,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0,
        growthRate: 0,
    });
    const [courseRevenue, setCourseRevenue] = useState([]);
    const [bookRevenue, setBookRevenue] = useState([]);
    const [methodRevenue, setMethodRevenue] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);

    useEffect(() => {
        if (userProfile && userProfile.role === 'superadmin') {
            fetchData();
        }
    }, [userProfile]);

    useEffect(() => {
        if (payments.length >= 0 && courses.length >= 0) {
            calculateStats(payments, courses, dateRange);
        }
    }, [payments, courses, dateRange]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [paymentsData, coursesData] = await Promise.all([
                getAllPayments(userProfile.role, userProfile.id),
                getAllCoursesByRole(userProfile.role, userProfile.id)
            ]);

            const validPayments = (paymentsData || []).filter(p => {
                const status = (p.paymentStatus || '').toLowerCase();
                return status === 'completed' || status === 'paid';
            });
            setPayments(validPayments);
            setCourses(coursesData || []);
            calculateStats(validPayments, coursesData || []);
        } catch (error) {
            console.error('Error fetching revenue data:', error);
        }
        setIsLoading(false);
    };

    const calculateStats = (validPayments, coursesData, selectedDateRange = [null, null]) => {
        let total = 0;
        let thisMonth = 0;
        let lastMonth = 0;

        const currentMonthStart = dayjs().startOf('month');
        const lastMonthStart = dayjs().subtract(1, 'month').startOf('month');
        const lastMonthEnd = dayjs().subtract(1, 'month').endOf('month');

        const courseTotals = {};
        const methodTotals = {};
        const [startDate, endDate] = selectedDateRange;

        validPayments.forEach(payment => {
            const amount = parseFloat(payment.amount || 0);
            const paymentDate = dayjs(payment.paymentDate || payment.createdAt);
            
            if (startDate && paymentDate.isBefore(startDate, 'day')) return;
            if (endDate && paymentDate.isAfter(endDate, 'day')) return;

            total += amount;
            
            if (paymentDate.isAfter(currentMonthStart) || paymentDate.isSame(currentMonthStart)) {
                thisMonth += amount;
            } else if ((paymentDate.isAfter(lastMonthStart) || paymentDate.isSame(lastMonthStart)) && (paymentDate.isBefore(lastMonthEnd) || paymentDate.isSame(lastMonthEnd))) {
                lastMonth += amount;
            }

            // Group by course
            if (payment.courseId) {
                if (!courseTotals[payment.courseId]) {
                    courseTotals[payment.courseId] = 0;
                }
                courseTotals[payment.courseId] += amount;
            }

            // Group by payment method
            let method = (payment.paymentMethod || 'Unknown').trim();
            const methodLower = method.toLowerCase();
            if (methodLower === 'bank' || methodLower === 'bank deposit') {
                method = 'Bank Deposit';
            } else if (methodLower === 'cash') {
                method = 'Cash';
            } else if (methodLower === 'card') {
                method = 'Card';
            } else if (methodLower === 'directpay_card' || methodLower.includes('directpay')) {
                method = 'DirectPay';
            }

            if (!methodTotals[method]) {
                methodTotals[method] = 0;
            }
            methodTotals[method] += amount;
        });

        let growth = 0;
        if (lastMonth > 0) {
            growth = ((thisMonth - lastMonth) / lastMonth) * 100;
        } else if (thisMonth > 0) {
            growth = 100;
        }

        setStats({
            totalRevenue: total,
            thisMonthRevenue: thisMonth,
            lastMonthRevenue: lastMonth,
            growthRate: growth.toFixed(2),
        });

        // Map course and book revenues to array for table
        const courseRevList = [];
        const bookRevList = [];

        Object.keys(courseTotals).forEach(courseId => {
            const course = coursesData.find(c => String(c.id) === String(courseId));
            const revItem = {
                key: courseId,
                courseName: course ? course.name : 'Unknown',
                revenue: courseTotals[courseId]
            };

            if (course && course.type === 'book') {
                bookRevList.push(revItem);
            } else {
                courseRevList.push(revItem);
            }
        });

        setCourseRevenue(courseRevList.sort((a, b) => b.revenue - a.revenue));
        setBookRevenue(bookRevList.sort((a, b) => b.revenue - a.revenue));

        // Map method revenues to array for table
        const methodRevArray = Object.keys(methodTotals).map(method => ({
            key: method,
            methodName: method,
            revenue: methodTotals[method]
        })).sort((a, b) => b.revenue - a.revenue);

        setMethodRevenue(methodRevArray);
    };

    const columns = [
        {
            title: 'Course Name',
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: 'Total Revenue (LKR)',
            dataIndex: 'revenue',
            key: 'revenue',
            render: (value) => `Rs ${value.toLocaleString()}`,
            sorter: (a, b) => a.revenue - b.revenue,
        }
    ];

    const bookColumns = [
        {
            title: 'Book Name',
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: 'Total Revenue (LKR)',
            dataIndex: 'revenue',
            key: 'revenue',
            render: (value) => `Rs ${value.toLocaleString()}`,
            sorter: (a, b) => a.revenue - b.revenue,
        }
    ];

    const methodColumns = [
        {
            title: 'Payment Method',
            dataIndex: 'methodName',
            key: 'methodName',
        },
        {
            title: 'Total Revenue (LKR)',
            dataIndex: 'revenue',
            key: 'revenue',
            render: (value) => `Rs ${value.toLocaleString()}`,
            sorter: (a, b) => a.revenue - b.revenue,
        }
    ];

    if (isLoading) {
        return (
            <div className="revenue-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="revenue-analysis">
            <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1>Revenue Analysis</h1>
                <RangePicker 
                    onChange={(dates) => setDateRange(dates || [null, null])} 
                    style={{ width: 300 }}
                />
            </div>

            <Row gutter={[16, 16]} className="stats-row">
                <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="Total Revenue (All Time)"
                            value={stats.totalRevenue}
                            precision={2}
                            prefix="Rs "
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="This Month's Revenue"
                            value={stats.thisMonthRevenue}
                            precision={2}
                            prefix="Rs "
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="Month-over-Month Growth"
                            value={Math.abs(stats.growthRate)}
                            precision={2}
                            valueStyle={{ color: stats.growthRate >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix={stats.growthRate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                            suffix="%"
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                    <div className="table-container" style={{ height: '100%' }}>
                        <Title level={4}>Revenue by Course</Title>
                        <Table 
                            dataSource={courseRevenue} 
                            columns={columns} 
                            pagination={{ pageSize: 10 }}
                            bordered
                        />
                    </div>
                </Col>
                <Col xs={24} lg={8}>
                    <div className="table-container" style={{ height: '100%' }}>
                        <Title level={4}>Revenue by Book</Title>
                        <Table 
                            dataSource={bookRevenue} 
                            columns={bookColumns} 
                            pagination={{ pageSize: 10 }}
                            bordered
                        />
                    </div>
                </Col>
                <Col xs={24} lg={8}>
                    <div className="table-container" style={{ height: '100%' }}>
                        <Title level={4}>Revenue by Payment Method</Title>
                        <Table 
                            dataSource={methodRevenue} 
                            columns={methodColumns} 
                            pagination={{ pageSize: 10 }}
                            bordered
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default RevenueAnalysis;
